import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18n';
import { cgToast, notifyError } from 'modules/Common/ui/toast';
import { parsePublicationBlockers } from './supplierPublicationBlockers';
import {
  confirmSupplierDraft,
  createSupplierDraft,
  discardSupplierDraft,
  fetchSupplierDraft,
  fetchSupplierDrafts,
  patchSupplierDraft,
} from './supplierDraftApi';
import { SUPPLIER_FACTORS_QUERY_KEY } from './useSupplierFactors';
import { SUPPLIER_INBOX_QUERY_KEY } from './useSupplierInbox';

export const SUPPLIER_DRAFTS_QUERY_KEY = ['supplierDrafts'];
export const supplierDraftQueryKey = (draftId) => [...SUPPLIER_DRAFTS_QUERY_KEY, draftId];

export const useSupplierDrafts = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: SUPPLIER_DRAFTS_QUERY_KEY,
    queryFn: fetchSupplierDrafts,
    staleTime: 0,
    enabled,
  });

export const useSupplierDraft = (draftId, { enabled = true } = {}) =>
  useQuery({
    queryKey: supplierDraftQueryKey(draftId),
    queryFn: () => fetchSupplierDraft(draftId),
    enabled: enabled && !!draftId,
    staleTime: 0,
  });

// S10 "draft-by-default": SupplierEmissionsModal's injected http.post routes
// here instead of publishSupplierFactor when the supplier picks "Save as
// draft" on a NEW entry. Deliberately UNLIKE patch/discard/confirm below —
// no notify() and no onError here — because, unlike those (invoked directly
// by page-level buttons), this mutation is called from INSIDE the reused
// InstallationRealEmissionsModal's own submit flow, which already owns a
// single success toast (SupplierEmissionsModal overrides its text via
// createSuccessMessage) and a single error catch (→ notifyErrorClient). Both
// here too would double the toast.
export const useCreateSupplierDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createSupplierDraft(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SUPPLIER_DRAFTS_QUERY_KEY }),
  });
};

// patch/discard/confirm mutations for the draft review surface. Every mutation
// keeps `notifyError` as the baseline failure signal (consistent with the rest
// of the module — see useSupplierFactors/useSupplierInbox), while the review
// page ALSO reads `mutation.error` directly to render the specific inline
// guidance the design calls for (409 conflict on patch, 422 overlap on
// confirm) without closing the form or discarding entered values.
export const useSupplierDraftActions = () => {
  const queryClient = useQueryClient();

  const invalidateDraft = async (draftId) => {
    await queryClient.invalidateQueries({ queryKey: SUPPLIER_DRAFTS_QUERY_KEY });
    if (draftId) {
      await queryClient.invalidateQueries({ queryKey: supplierDraftQueryKey(draftId) });
    }
  };

  const patch = useMutation({
    mutationFn: ({ draftId, body }) => patchSupplierDraft(draftId, body),
    onSuccess: async (_data, vars) => {
      cgToast.success(i18n.t('freeSupplier:toasts.savedTitle', 'Saved'), {
        description: i18n.t('freeSupplier:toasts.draftSavedBody', 'Your changes were saved'),
      });
      await invalidateDraft(vars.draftId);
    },
    onError: notifyError,
  });

  const discard = useMutation({
    mutationFn: (draftId) => discardSupplierDraft(draftId),
    onSuccess: async (_data, draftId) => {
      cgToast.success(i18n.t('freeSupplier:toasts.discardedTitle', 'Discarded'), {
        description: i18n.t('freeSupplier:toasts.draftDiscardedBody', 'The proposal was discarded'),
      });
      await invalidateDraft(draftId);
    },
    onError: notifyError,
  });

  // Confirm invalidates the published factor list (a new/superseding revision
  // now exists), the drafts list/detail (reviewState → published) and the
  // inbox (fulfilment coverage can change). The success toast + navigation are
  // owned by the caller (DraftReviewPage) since only it knows where to go.
  const confirm = useMutation({
    mutationFn: (draftId) => confirmSupplierDraft(draftId),
    onSuccess: async (_data, draftId) => {
      await Promise.all([
        invalidateDraft(draftId),
        queryClient.invalidateQueries({ queryKey: SUPPLIER_FACTORS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: SUPPLIER_INBOX_QUERY_KEY }),
      ]);
    },
    onError: (error) => {
      // A publication-requirements failure is rendered IN FULL by the review
      // page (PublicationBlockersPanel — every blocker, plus where to fix it),
      // so the toast is suppressed for that one case: it would repeat the same
      // sentence in a surface that can only show its beginning. Every other
      // failure keeps the baseline toast.
      if (parsePublicationBlockers(error)) return;
      cgToast.apiError(error);
    },
  });

  return { patch, discard, confirm };
};
